import React, { useState, useEffect, useRef } from "react"
import defaultCompanyLogoUrl from "~/images/default_company_logo.svg"
import { CollapsableFilter } from "./collapsable_filter"
import {
  getCompanyLogoUrl,
  hostnameFromURL,
  isADomainName,
} from "./domain-helpers"

const t = (key: string, options?: Record<string, unknown>) =>
  window.i18n.t(key, options)

interface CompanyNameFilterProps {
  value: string[]
  onChange: (value: string[]) => void
}

interface CompanySuggestion {
  domain: string
  name: string
  email_count: number
}

// Helper to build API URL - uses session authentication automatically
function buildDomainsSuggestionUrl(query: string): string {
  return `/v2/domains-suggestion?query=${encodeURIComponent(query)}`
}

export function CompanyNameFilter({
  value = [],
  onChange,
}: CompanyNameFilterProps) {
  const [inputValue, setInputValue] = useState("")
  const [suggestions, setSuggestions] = useState<CompanySuggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  // Map of domain -> company name for display enhancement
  const [companyNames, setCompanyNames] = useState<Record<string, string>>({})
  const inputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)
  const fetchedDomainsRef = useRef<Set<string>>(new Set())

  // Fetch company names for domains we haven't fetched yet
  useEffect(() => {
    const domainsToFetch = value.filter(
      (domain) => !fetchedDomainsRef.current.has(domain),
    )
    if (domainsToFetch.length === 0) return

    // Mark these domains as being fetched
    domainsToFetch.forEach((domain) => fetchedDomainsRef.current.add(domain))

    const controller = new AbortController()

    const fetchCompanyData = async () => {
      const results = await Promise.all(
        domainsToFetch.map(async (domain): Promise<[string, string]> => {
          try {
            const response = await fetch(buildDomainsSuggestionUrl(domain), {
              signal: controller.signal,
            })
            const data = await response.json()
            if (data.data && data.data.length > 0) {
              return [domain, data.data[0].name]
            }
            return [domain, domain]
          } catch {
            // Silently handle fetch errors - fallback to domain name
            return [domain, domain]
          }
        }),
      )
      if (!controller.signal.aborted) {
        setCompanyNames((prev) => ({
          ...prev,
          ...Object.fromEntries(results),
        }))
      }
    }
    fetchCompanyData()

    return () => controller.abort()
  }, [value])

  // Fetch suggestions from API
  useEffect(() => {
    const controller = new AbortController()

    const fetchSuggestions = async () => {
      if (inputValue.length < 3) {
        setSuggestions([])
        setShowSuggestions(false)
        return
      }

      const hostname = hostnameFromURL(inputValue)
      if (hostname === "") {
        setSuggestions([])
        setShowSuggestions(false)
        return
      }

      try {
        const response = await fetch(buildDomainsSuggestionUrl(hostname), {
          signal: controller.signal,
        })
        const data = await response.json()
        if (data.data) {
          setSuggestions(data.data)
          setShowSuggestions(true)
        }
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          // Silently handle fetch errors - clear suggestions
          setSuggestions([])
        }
      }
    }

    const timeoutId = setTimeout(fetchSuggestions, 300)
    return () => {
      clearTimeout(timeoutId)
      controller.abort()
    }
  }, [inputValue])

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleSelectSuggestion = (suggestion: CompanySuggestion) => {
    if (!value.includes(suggestion.domain)) {
      onChange([...value, suggestion.domain])
      // Store company name immediately
      setCompanyNames((prev) => ({
        ...prev,
        [suggestion.domain]: suggestion.name,
      }))
      fetchedDomainsRef.current.add(suggestion.domain)
    }
    setInputValue("")
    setSuggestions([])
    setShowSuggestions(false)
  }

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault()
      const domainName = hostnameFromURL(inputValue)

      if (isADomainName(domainName) && !value.includes(domainName)) {
        // Fetch company name from API
        try {
          const response = await fetch(buildDomainsSuggestionUrl(domainName))
          const data = await response.json()
          const actualDomain =
            data.data && data.data.length > 0 ? data.data[0].domain : domainName
          const companyName =
            data.data && data.data.length > 0
              ? data.data[0].name
              : domainName
          onChange([...value, actualDomain])
          setCompanyNames((prev) => ({
            ...prev,
            [actualDomain]: companyName,
          }))
          fetchedDomainsRef.current.add(actualDomain)
        } catch {
          // Silently handle fetch errors - use domain name as fallback
          onChange([...value, domainName])
          setCompanyNames((prev) => ({
            ...prev,
            [domainName]: domainName,
          }))
          fetchedDomainsRef.current.add(domainName)
        }
      }

      setInputValue("")
      setSuggestions([])
      setShowSuggestions(false)
    }
  }

  const handleRemoveCompany = (domain: string) => {
    onChange(value.filter((v) => v !== domain))
  }

  const handleClear = () => {
    onChange([])
    setInputValue("")
  }

  const hasValues = value.length > 0

  return (
    <CollapsableFilter
      label={t("js.back.discover.filters.company_name.title")}
      icon="building"
      tooltip={t("js.back.discover.filters.company_name.tooltip")}
      count={value.length}
      hasValues={hasValues}
      onClear={handleClear}
    >
      <label className="sr-only" htmlFor="discover-domains">
        {t("js.back.discover.filters.company_name.title")}
      </label>
      <div className="position-relative">
        <input
          ref={inputRef}
          id="discover-domains"
          type="text"
          className="h-input h-input--block mb-2"
          placeholder={t("js.back.discover.filters.company_name.placeholder")}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setShowSuggestions(true)
          }}
          autoComplete="off"
        />

        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="autocomplete-suggestions position-absolute start-0 end-0"
            style={{
              top: "100%",
              zIndex: 1000,
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            {suggestions.map((suggestion) => (
              <div
                key={suggestion.domain}
                className="autocomplete-suggestion"
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                <span className="autocomplete-logo">
                  <img
                    src={getCompanyLogoUrl(suggestion.domain)}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.src = defaultCompanyLogoUrl
                    }}
                  />
                </span>
                <span className="autocomplete-value">
                  {suggestion.name || suggestion.domain}
                </span>
                <span className="autocomplete-data">{suggestion.domain}</span>
                <span className="tag autocomplete-count">
                  {t("js.back.discover.filters.result", {
                    count: suggestion.email_count,
                  })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {value.length > 0 && (
        <div>
          {value.map((domain) => (
            <div key={domain} className="company-filter-entry mb-1">
              <img
                className="company-filter-entry__logo"
                src={getCompanyLogoUrl(domain)}
                alt=""
                onError={(e) => {
                  e.currentTarget.src = defaultCompanyLogoUrl
                }}
              />
              <span className="company-filter-entry__name">
                {companyNames[domain] || domain}
              </span>
              <span className="company-filter-entry__url">{domain}</span>
              <button
                type="button"
                className="h-button h-button--ghost h-button--xs h-button--icon"
                aria-label={t("js.back.discover.filters.remove")}
                onClick={() => handleRemoveCompany(domain)}
              >
                <span className="far fa-times" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
    </CollapsableFilter>
  )
}
